import { Timestamp } from 'firebase/firestore';
import { CompanyModel, CompanyPlan, CompanyRelationship, CompanySettingsModel, IntegrationTokens, UserMembershipTypeModel } from '@mesterplan/shared-types';
import type { CompanyDto, CompanyIntegrationStatusDto, CompanyIntegrationsDto, MyCompanyDto, SupplierSessionCompanyDto } from '@mesterplan/api-client';
import type { QueryStatus } from '@mesterplan/web-hooks';

/**
 * Normalises a membership-list role string (`MyCompanyDto.role`) to the typed
 * union. The .NET membership stores lowercase `owner|admin|user`; anything else
 * (unknown/absent) becomes `null` so callers fall through to the permission-set
 * check rather than mis-grant owner/admin UI.
 */
export function toMembershipRole(role?: string | null): UserMembershipTypeModel | null {
  switch (role?.toLowerCase()) {
    case 'owner':
      return 'owner';
    case 'admin':
      return 'admin';
    case 'user':
      return 'user';
    default:
      return null;
  }
}

/** CompanyModel.plan only distinguishes "partner" and "supplier" from everything else. */
export function toPlan(plan?: string | null): CompanyPlan {
  switch (plan?.toLowerCase()) {
    case 'partner':
      return 'partner';
    case 'supplier':
      return 'supplier';
    default:
      return 'full';
  }
}

/** An API that predates supplier access sends no relationship: that is a plain member. */
export function toRelationship(relationship?: string | null): CompanyRelationship {
  return relationship?.toLowerCase() === 'supplier' ? 'supplier' : 'member';
}

export function toTimestamp(iso?: string | null): Timestamp {
  const date = iso ? new Date(iso) : new Date(0);
  return Timestamp.fromDate(Number.isNaN(date.getTime()) ? new Date(0) : date);
}

// CompanyModel requires non-optional createdAt/updatedAt, but the membership-list
// DTO (MyCompanyDto) carries no company timestamps — only the user's joinedAt,
// which is a different thing. No consumer reads these off the switcher rows, so
// use an explicit epoch placeholder rather than passing joinedAt off as the
// company's creation/modification time.
export const UNKNOWN_TIMESTAMP = Timestamp.fromDate(new Date(0));
const UNKNOWN_ISO = new Date(0).toISOString();

// Lightweight row for the company switcher — the membership list endpoint only
// carries identity + plan. settings/integrations are filled by the full read of
// the selected company (mapCompanyDto) once it is the active org.
export function mapMyCompany(dto: MyCompanyDto): CompanyModel {
  return {
    id: dto.id,
    name: dto.name,
    vat: '',
    address: '',
    userMemberships: [],
    settings: {},
    plan: toPlan(dto.plan),
    ...supplierFields(dto),
    createdAt: UNKNOWN_TIMESTAMP,
    updatedAt: UNKNOWN_TIMESTAMP,
  };
}

// Only set for a supplier row, so a plain member row keeps its exact old shape.
function supplierFields(dto: MyCompanyDto): Pick<CompanyModel, 'relationship' | 'supplierCompanyId'> {
  if (toRelationship(dto.relationship) !== 'supplier') return {};
  if (!dto.supplierCompanyId) return { relationship: 'supplier' };
  return { relationship: 'supplier', supplierCompanyId: dto.supplierCompanyId };
}

// Maps the backend integration status (flags only — no tokens) to the
// `IntegrationTokens` shape consumers read (`integrations.economic.isConnected`,
// `integrations[id]?.isConnected`). `connectedAt`/`lastSyncedAt` are only set
// when present so consumers can distinguish "never" from the epoch sentinel.
function toIntegrationStatus(status: CompanyIntegrationStatusDto): IntegrationTokens[string] {
  return {
    isConnected: status.isConnected,
    ...(status.connectedAt ? { connectedAt: toTimestamp(status.connectedAt) } : {}),
    ...(status.lastSyncedAt ? { lastSyncedAt: toTimestamp(status.lastSyncedAt) } : {}),
  };
}

function toIntegrationTokens(integrations: CompanyIntegrationsDto | null | undefined): IntegrationTokens {
  if (!integrations) {
    return {};
  }
  return {
    economic: toIntegrationStatus(integrations.economic),
    dinero: toIntegrationStatus(integrations.dinero),
    googleCalendar: toIntegrationStatus(integrations.googleCalendar),
    danlon: toIntegrationStatus(integrations.danlon),
  };
}

// Full company record for the active org. `settings` is the parsed settingsJson
// blob (defaulted to `{}` so the ~100 `selectedCompany.settings?.…` consumers
// never read off `undefined`). `integrations` carries the backend connection
// flags (economic/dinero/googleCalendar). `userMemberships` has no .NET source
// yet — left empty; every consumer optional-chains it.
export function mapCompanyDto(dto: CompanyDto): CompanyModel {
  let settings: CompanySettingsModel = {};
  if (dto.settingsJson) {
    try {
      const parsed: unknown = JSON.parse(dto.settingsJson);
      // Must be a plain object — typeof is 'object' for arrays and null too, and
      // assigning an array as settings would break CompanySettingsModel consumers.
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        settings = parsed as CompanySettingsModel;
      }
    } catch {
      // Malformed blob — keep the empty default so consumers don't crash.
    }
  }
  return {
    id: dto.id,
    name: dto.name,
    vat: dto.vat,
    address: dto.address,
    city: dto.city ?? undefined,
    phone: dto.phone ?? undefined,
    email: dto.email ?? undefined,
    website: dto.website ?? undefined,
    bankName: dto.bankName ?? undefined,
    bankAccount: dto.bankAccount ?? undefined,
    fiKode: dto.fiKode ?? undefined,
    iban: dto.iban ?? undefined,
    swift: dto.swift ?? undefined,
    latePaymentInterestRate: dto.latePaymentInterestRate ?? undefined,
    logoUrl: dto.logoUrl ?? undefined,
    plan: toPlan(dto.plan),
    settings,
    integrations: toIntegrationTokens(dto.integrations),
    userMemberships: [],
    createdAt: toTimestamp(dto.createdAt),
    updatedAt: toTimestamp(dto.updatedAt),
  };
}

/**
 * Pure derivation of the context's loading/partner flags. Extracted so the
 * loading × error × empty × populated matrix is unit-testable without rendering.
 *
 * `isPartnerUser` must gate on the companies query having actually *succeeded*
 * with an empty list — a transient error also yields `companiesCount === 0`, and
 * treating that as "no memberships" would wrongly drop the user into partner UI.
 */
export function deriveCompanyFlags(args: {
  authStatus: 'initializing' | 'authenticated' | 'unauthenticated';
  companiesStatus: QueryStatus;
  companyStatus: QueryStatus;
  companiesCount: number;
  hasActiveOrg: boolean;
}): { isLoadingCompanies: boolean; isPartnerUser: boolean } {
  const { authStatus, companiesStatus, companyStatus, companiesCount, hasActiveOrg } = args;
  const settled = (s: QueryStatus) => s === 'success' || s === 'error';
  const authenticated = authStatus === 'authenticated';

  const isLoadingCompanies =
    authStatus === 'initializing' ||
    (authenticated && !settled(companiesStatus)) ||
    (hasActiveOrg && !settled(companyStatus));

  const isPartnerUser = authenticated && companiesStatus === 'success' && companiesCount === 0;

  return { isLoadingCompanies, isPartnerUser };
}

/**
 * A supplier session is "access removed" only when the API said so (`org_not_available`) or the
 * active supplier row has vanished from a refreshed company list. Never from a bare 403.
 */
export function isSupplierAccessRemoved(args: { orgNotAvailable: boolean; sessionRowGone: boolean }): boolean {
  return args.orgNotAvailable || args.sessionRowGone;
}

/** A ProblemDetails `code` on the error (parsed or on the raw body): the API answered, it is not a missing route. */
export function hasProblemCode(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const direct = (error as { code?: unknown }).code;
  if (typeof direct === 'string' && direct.length > 0) return true;
  const body = (error as { body?: unknown }).body;
  const nested = typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : undefined;
  return typeof nested === 'string' && nested.length > 0;
}

/**
 * "Endpoint missing on an old backend": a 404 from the slim read WITHOUT a ProblemDetails code.
 * A 404 that carries a code is the API saying the company is not found (access removed).
 */
export function isSlimReadMissing(status: number, error: unknown): boolean {
  return status === 404 && !hasProblemCode(error);
}

/**
 * Is the calculation module on? Inside a supplier session the setting can be unknown (older
 * backend, or the slim read carried none): unknown is NOT "off" there. The AI-analyse surfaces
 * show and the permission filter decides. Outside a session unknown stays off, as before.
 */
export function calculationEnabledFor(company: CompanyModel | null | undefined, isSupplierSession: boolean): boolean {
  const enabled = company?.settings?.calculation?.enabled;
  return enabled ?? isSupplierSession;
}

/**
 * The company record inside a supplier session: only name, logo and the AI-analyse switch are
 * known (slim read). With no slim read (older backend, 404) the switcher row's name stands in
 * and `settings` stays empty, which the nav treats as "module setting unknown".
 */
export function sessionCompanyDto(row: MyCompanyDto, slim: SupplierSessionCompanyDto | null): CompanyDto {
  const enabled = slim?.settings?.calculation?.enabled;
  const settingsJson = enabled === undefined ? null : JSON.stringify({ calculation: { enabled } });
  return {
    id: row.id,
    name: slim?.name ?? row.name,
    vat: '',
    address: '',
    logoUrl: slim?.logoUrl ?? null,
    settingsJson,
    plan: row.plan,
    // The membership date is the user's, not the company's: epoch placeholder, as in mapMyCompany.
    createdAt: UNKNOWN_ISO,
    updatedAt: UNKNOWN_ISO,
  };
}
